/**
 * focus-model.js — the composite focus model (contracts §3).
 *
 * ── Why this exists ───────────────────────────────────────────────────────
 * Two roving-tabindex systems already consume arrow keys inside the bar: the
 * page-tab strip (`role="tablist"`) and the viewport radiogroup. A third,
 * bar-wide roving system would fight both — the arrow key would either move the
 * page tab AND the group cursor, or the group would swallow the key before the
 * widget saw it. So the model is COMPOSITE rather than uniform:
 *
 *   Tab      moves between GROUPS (and out of the bar, into the canvas)
 *   ←/→      move WITHIN the focused group
 *   Home/End jump to its ends
 *   a nested arrow-owning widget is ONE stop and keeps its own arrows
 *   Escape   inside such a widget returns focus to its group
 *
 * Without it a keyboard user pays ~30 Tab presses to cross a bar whose whole
 * design premise is that the anchors are targetable without looking.
 *
 * ── Stops are computed, never cached ──────────────────────────────────────
 * Row 2 morphs, the page strip collapses to one trigger, controls disable
 * themselves as locks arrive. A cached stop list is wrong within one frame of
 * any of those. So `_stops()` re-reads the DOM on every keystroke, and the only
 * thing applied eagerly is the roving `tabindex` — which Tab needs to be
 * correct BEFORE the user presses anything.
 *
 * ── Why the observer does not watch `tabindex` ────────────────────────────
 * This module writes `tabindex`. Watching it would re-enter on our own write.
 * We watch what OTHER code changes — children, `hidden`, `disabled` — and write
 * only when the value actually differs, so a re-entrant pass would converge
 * anyway. Belt and braces, because a focus manager that loops is a frozen tab.
 */

import { isHiddenOrInert } from '../tabbable.js';

const FOCUSABLE =
  'button, [href], input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';

/** Marks a widget that owns its own arrow keys and is ONE stop to its group. */
export const NESTED_ATTR = 'data-cb-nested';

/** Marks a group root. The value is the group id from contracts §3. */
export const GROUP_ATTR = 'data-cb-group';

function isHidden(el) {
  if (el.getAttribute('aria-hidden') === 'true') return true;
  // A dropdown's ITEMS are not controls of the group that owns its trigger. The
  // bar's menus sit in their region while closed (`ToolbarMenu` relocates them
  // into the popover shell on open), so without this every project-menu row
  // counted as a stop — ten of them, all `display: none`, and arrowing onto one
  // would have moved focus to something invisible. An open menu traps its own
  // focus and is a separate context by then.
  if (el.closest?.('[role="menu"]')) return true;
  // Hidden or inert — itself or an ancestor (the page strip hides exactly that
  // way when it collapses), structurally or by computed style. One answer for
  // every focus manager: ui/tabbable.js (BC-4). jsdom loads no stylesheets, so
  // the computed half is a no-op there; `command-bar-geometry.mjs` asserts the
  // rendered truth.
  return isHiddenOrInert(el);
}

function isDisabled(el) {
  return !!(el.disabled || el.getAttribute('aria-disabled') === 'true');
}

/**
 * The one stop a nested widget contributes: whatever it currently considers
 * current. Falls back to its first focusable child, so a widget that has not
 * settled yet is still reachable rather than silently skipped.
 */
function nestedRepresentative(nested) {
  const candidates = Array.from(nested.querySelectorAll(FOCUSABLE)).filter(
    (el) => !isHidden(el) && !isDisabled(el)
  );
  if (!candidates.length) return null;
  return (
    candidates.find(
      (el) =>
        el.getAttribute('aria-selected') === 'true' ||
        el.getAttribute('aria-checked') === 'true' ||
        el.hasAttribute('aria-current')
    ) ||
    candidates.find((el) => el.tabIndex === 0) ||
    candidates[0]
  );
}

export class CompositeFocusModel {
  /**
   * @param {Array<{id: string, label: string, el: HTMLElement}>} groups — in
   *   TAB ORDER. The order is the contract; DOM order must agree with it and
   *   `command-bar-tests.js` proves it does.
   */
  constructor(groups) {
    this._groups = groups.filter((g) => g && g.el);
    this._last = new Map(); // group id → the control it last had focus on
    this._frame = null;
    this._applying = false;
    this._observers = [];
    this._bound = [];

    for (const group of this._groups) {
      group.el.setAttribute(GROUP_ATTR, group.id);
      group.el.setAttribute('role', 'group');
      group.el.setAttribute('aria-label', group.label);

      const onKeydown = (e) => this._onKeydown(group, e);
      const onFocusIn = (e) => this._onFocusIn(group, e);
      group.el.addEventListener('keydown', onKeydown);
      group.el.addEventListener('focusin', onFocusIn);
      this._bound.push(() => {
        group.el.removeEventListener('keydown', onKeydown);
        group.el.removeEventListener('focusin', onFocusIn);
      });

      if (typeof MutationObserver === 'function') {
        const observer = new MutationObserver(() => this._schedule());
        observer.observe(group.el, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['hidden', 'disabled', 'aria-hidden', 'aria-disabled']
        });
        this._observers.push(observer);
      }
    }

    this.refresh();
  }

  destroy() {
    for (const off of this._bound) off();
    this._bound = [];
    for (const observer of this._observers) observer.disconnect();
    this._observers = [];
    if (this._frame != null) {
      cancelAnimationFrame(this._frame);
      this._frame = null;
    }
    this._last.clear();
    this._groups = [];
  }

  /** Group ids in tab order — what the gate reads. */
  get groupIds() {
    return this._groups.map((g) => g.id);
  }

  /** The stops one group currently offers, in DOM order. For tests. */
  stopsFor(groupId) {
    const group = this._groups.find((g) => g.id === groupId);
    return group ? this._stops(group) : [];
  }

  _schedule() {
    if (this._applying || this._frame != null) return;
    const raf =
      typeof requestAnimationFrame === 'function'
        ? requestAnimationFrame
        : (cb) => setTimeout(cb, 0);
    this._frame = raf(() => {
      this._frame = null;
      this.refresh();
    });
  }

  /**
   * Re-apply the roving tabindex: exactly one tabbable control per group.
   *
   * Idempotent by construction — it writes only where the value differs — which
   * is what lets the observer above be as coarse as it is.
   */
  refresh() {
    this._applying = true;
    try {
      for (const group of this._groups) {
        const stops = this._stops(group);
        if (!stops.length) continue;
        const remembered = this._last.get(group.id);
        const active = remembered && stops.includes(remembered) ? remembered : stops[0];
        this._last.set(group.id, active);
        for (const stop of stops) {
          const want = stop === active ? 0 : -1;
          if (stop.tabIndex !== want) stop.tabIndex = want;
        }
      }
    } finally {
      this._applying = false;
    }
  }

  /**
   * A group's stops: its focusable controls, with each nested arrow-owning
   * widget collapsed to the single control it currently considers current.
   */
  _stops(group) {
    const out = [];
    const seenNested = new Set();
    for (const el of group.el.querySelectorAll(FOCUSABLE)) {
      if (isHidden(el) || isDisabled(el)) continue;
      const nested = el.closest(`[${NESTED_ATTR}]`);
      if (nested && group.el.contains(nested)) {
        if (seenNested.has(nested)) continue;
        seenNested.add(nested);
        const rep = nestedRepresentative(nested);
        if (rep) out.push(rep);
        continue;
      }
      out.push(el);
    }
    return out;
  }

  _onFocusIn(group, event) {
    const target = event.target;
    if (!target || !group.el.contains(target)) return;
    const nested = target.closest?.(`[${NESTED_ATTR}]`);
    // Focus landing INSIDE a nested widget still belongs to the group — the
    // widget's own current item is the group's stop, whichever child it is.
    const stop = nested && group.el.contains(nested) ? nestedRepresentative(nested) : target;
    if (!stop) return;
    this._last.set(group.id, stop);
    // Make the newly focused control the group's tabbable one, so leaving and
    // returning by Tab lands where the user left off rather than at the start.
    this._schedule();
  }

  _onKeydown(group, event) {
    if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;
    const target = event.target;
    if (!target || !group.el.contains(target)) return;
    const nested = target.closest?.(`[${NESTED_ATTR}]`);
    const insideNested = !!(nested && group.el.contains(nested));

    if (event.key === 'Escape') {
      // Contracts §3: Escape inside a nested widget returns focus to the group.
      // Only ever a nested widget — Escape everywhere else in the bar belongs to
      // whatever popover, rename field or modal is listening for it, and a focus
      // manager that eats Escape is a dialog that cannot be closed.
      if (!insideNested) return;
      const stops = this._stops(group);
      const rep = nestedRepresentative(nested);
      const outside = stops.find((s) => s !== rep);
      if (!outside) return;
      event.preventDefault();
      event.stopPropagation();
      this._focus(group, outside);
      return;
    }

    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    // The nested widget owns its arrows. Home/End too: inside the page strip
    // they mean "first/last page", which is what a tablist is expected to do.
    if (insideNested) return;

    const stops = this._stops(group);
    if (stops.length < 2) return;
    const index = stops.indexOf(target);
    if (index === -1) return;

    let next;
    if (event.key === 'ArrowRight') next = (index + 1) % stops.length;
    else if (event.key === 'ArrowLeft') next = (index - 1 + stops.length) % stops.length;
    else if (event.key === 'Home') next = 0;
    else next = stops.length - 1;
    if (next === index) return;

    event.preventDefault();
    this._focus(group, stops[next]);
  }

  _focus(group, stop) {
    this._last.set(group.id, stop);
    this.refresh();
    stop.focus?.();
  }
}
